import React, { useEffect } from 'react';
import { Icon } from '@iconify/react';
import { MessagePreviewContent, type MessagePreviewContentProps } from './MessagePreviewContent';

export interface MessagePreviewRailProps {
  /** Whether the rail is expanded. Collapsed width is 0 (board takes full width). */
  open: boolean;
  onClose: () => void;
  /** Preview content props, or null when nothing is selected. */
  content: MessagePreviewContentProps | null;
  /** Optional override node (e.g. a SequenceBoard for outreaching leads). */
  children?: React.ReactNode;
}

/**
 * MessagePreviewRail — an INLINE preview column for full-bleed views (Calendar,
 * Kanban). Unlike a slide-over drawer, it lives in the document flow: when it
 * opens it takes ~38% of the width and the board shrinks to fill the rest. No
 * scrim, no blur, no overlay — the board stays fully visible and interactive so
 * the user can keep scanning and clicking other items without losing context.
 *
 * The open/close transition is a width animation (transform for perf), keeping
 * the rail a navigation tool rather than a dead-end.
 */
export const MessagePreviewRail: React.FC<MessagePreviewRailProps> = ({ open, onClose, content, children }) => {
  // Esc closes (only while open). Kept lightweight — no scroll lock, since the
  // board behind must remain scrollable/clickable.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <aside
      aria-hidden={!open}
      className={`relative flex shrink-0 flex-col overflow-hidden bg-black/[0.34] shadow-[inset_1px_0_0_rgba(255,255,255,0.028)] transition-[width] duration-300 ease-[cubic-bezier(0.23,1,0.32,1)] ${
        open ? 'w-[43%] min-w-[340px] max-w-[500px]' : 'w-0 min-w-0 shadow-none'
      }`}
    >
      {/* When a `children` override is passed (e.g. SequenceBoard for the
          Outreach lens), it renders its OWN full header (title + lead context
          + close button), so we suppress this rail header to avoid showing the
          "Send plan / close" bar twice. The rail header only shows for the
          plain Conversation preview. */}
      {!children && (
        <div className="flex items-center justify-between px-4 py-2.5 shadow-[inset_0_-1px_0_rgba(255,255,255,0.024)]">
          <span className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-white/40">
            <Icon icon="bx:detail" className="h-3.5 w-3.5" />
            Conversation
          </span>
          <button type="button" onClick={onClose} aria-label="Close preview" className="flex h-8 w-8 items-center justify-center rounded-lg text-white/45 transition-colors hover:bg-white/[0.06] hover:text-white">
            <Icon icon="bx:x" className="h-5 w-5" />
          </button>
        </div>
      )}
      <div className="min-h-0 flex-1">{open && (children || (content && <MessagePreviewContent {...content} onClose={undefined} />))}</div>
    </aside>
  );
};

export default MessagePreviewRail;
